import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
  faArrowUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons";
import { getScanEventColor } from "../mockData";
import { formatEventTime, parseDisplayTimestamp } from "../utils";

const trackingBadgeMap = {
  Complete:
    "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200",
  Delivered:
    "bg-green-100 text-green-700 ring-1 ring-green-200",
  "In Transit":
    "bg-blue-100 text-blue-700 ring-1 ring-blue-200",
  "Picked Up":
    "bg-sky-100 text-sky-700 ring-1 ring-sky-200",
  Booked:
    "bg-orange-100 text-orange-700 ring-1 ring-orange-200",
  Exception:
    "bg-red-100 text-red-700 ring-1 ring-red-200",
};

// Total table columns (must match DifotTools header count)
const COL_SPAN = 20;

const getMachshipConsignmentUrl = (msNumber = "") => {
  const machshipId = msNumber.replace(/^MS/i, "");
  if (!machshipId) return null;
  return `https://live.machship.com/#/createmanage/consignments/view/${machshipId}`;
};

const historySourceLabelMap = {
  carrier: "Carrier Integration",
  kpi: "Consignment KPI",
};

const formatHistorySource = (value = "") => {
  const normalized = String(value || "").trim().toLowerCase();
  return historySourceLabelMap[normalized] || value || "-";
};

export default function ConsignmentRow({
  consignment,
  isSelected,
  reasonOptions = [],
  onToggleSelect,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [specialInstructionPos, setSpecialInstructionPos] = useState(null);
  const [kpiHistoryPos, setKpiHistoryPos] = useState(null);
  const specialInstructionBtnRef = useRef(null);
  const kpiHistoryBtnRef = useRef(null);
  const specialInstructionPopoverRef = useRef(null);
  const kpiHistoryPopoverRef = useRef(null);

  const scanEvents = normalizeScanEvents(consignment.scanEvents).reverse();
  const companyName = consignment.company || "";
  const parentName =
    consignment.parentCompany || companyName.split(" - ")[0] || "-";
  const reasonKey = consignment.reasonId || consignment.reason;
  const reasonLabel =
    reasonOptions.find((reason) => reason.value === reasonKey)?.label ||
    consignment.reason ||
    reasonKey ||
    "-";
  const hasReason = reasonLabel !== "-";
  const specialInstruction = consignment.specialInstruction?.trim() || "";
  const latestScanEvent = scanEvents[scanEvents.length - 1] ?? null;
  const trackingLabel =
    latestScanEvent?.detail ||
    latestScanEvent?.event ||
    "-";
  const badge =
    trackingBadgeMap[trackingLabel] ||
    "bg-gray-100 text-gray-700 ring-1 ring-gray-200";
  const latestScanLabel =
    latestScanEvent?.detail || latestScanEvent?.event || trackingLabel;
  const hasKpiHitValue =
    consignment.kpiHit !== null && consignment.kpiHit !== undefined;
  const kpiHitLabel = hasKpiHitValue ? (consignment.kpiHit ? "Yes" : "No") : "-";
  const hasIsProcessedValue =
    consignment.isProcessed !== null && consignment.isProcessed !== undefined;
  const isProcessedLabel = hasIsProcessedValue
    ? (consignment.isProcessed ? "Yes" : "No")
    : "-";
  const attachments = normalizeAttachments(consignment.attachment);
  const attachmentCount = normalizeAttachmentCount(consignment.attachmentCount);
  const serviceFlags = normalizeServiceFlags(consignment.serviceFlags);
  const hasServiceFlags = serviceFlags.length > 0;
  const { kpiMeasureDisplay, kpiMeasureIsLate, kpiMeasureHasValue } = formatKpiMeasureValue(consignment.kpiMeasure);
  const kpiUpdateHistoryEntries = Array.isArray(consignment.kpiUpdateHistory)
    ? consignment.kpiUpdateHistory
    : consignment.kpiUpdateHistory
    ? [consignment.kpiUpdateHistory]
    : [];
  const sortedKpiUpdateHistoryEntries = [...kpiUpdateHistoryEntries].sort((a, b) => {
    const left = parseDisplayTimestamp(a.updatedAt);
    const right = parseDisplayTimestamp(b.updatedAt);
    if (left === null && right === null) return 0;
    if (left === null) return 1;
    if (right === null) return -1;
    return right - left;
  });
  const hasKpiUpdateHistory = sortedKpiUpdateHistoryEntries.length > 0;
  const rowBg = isSelected
    ? "bg-[#144677]/5"
    : isExpanded
    ? "bg-blue-50/40"
    : "";
  const machshipUrl = getMachshipConsignmentUrl(consignment.msNumber);

  useEffect(() => {
    if (!specialInstructionPos) return;
    const handler = (e) => {
      if (
        specialInstructionBtnRef.current?.contains(e.target) ||
        specialInstructionPopoverRef.current?.contains(e.target)
      ) return;
      setSpecialInstructionPos(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [specialInstructionPos]);

  useEffect(() => {
    if (!kpiHistoryPos) return;
    const handler = (e) => {
      if (
        kpiHistoryBtnRef.current?.contains(e.target) ||
        kpiHistoryPopoverRef.current?.contains(e.target)
      ) return;
      setKpiHistoryPos(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [kpiHistoryPos]);

  const openSpecialInstruction = () => {
    if (specialInstructionPos) { setSpecialInstructionPos(null); return; }
    const rect = specialInstructionBtnRef.current?.getBoundingClientRect();
    if (rect) setSpecialInstructionPos({ top: rect.bottom + 4, left: rect.left });
  };

  const openKpiHistory = () => {
    if (kpiHistoryPos) { setKpiHistoryPos(null); return; }
    const rect = kpiHistoryBtnRef.current?.getBoundingClientRect();
    if (rect) setKpiHistoryPos({ top: rect.bottom + 4, left: rect.left });
  };

  return (
    <>
      {/* ── Main row ──────────────────────────────────────────────── */}
      <tr
        className={`border-b border-gray-100 hover:bg-blue-50/30 transition-colors duration-150 ${rowBg}`}
      >
        {/* Checkbox */}
        <td className="sticky left-0 bg-inherit w-10 px-3 py-2.5 z-10">
          <input
            type="checkbox"
            checked={!!isSelected}
            onChange={() => onToggleSelect(consignment.id)}
            className="difot-checkbox-light"
          />
        </td>

        {/* MS ID */}
        <td className="px-3 py-2.5 whitespace-nowrap max-w-[150px]">
          <OverflowTooltip
            value={consignment.msNumber}
            textClassName="text-[#144677] font-semibold text-xs hover:underline"
          >
            {machshipUrl ? (
              <a
                href={machshipUrl}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1"
              >
                <span className="truncate">{consignment.msNumber}</span>
                <FontAwesomeIcon
                  icon={faArrowUpRightFromSquare}
                  className="w-2.5 h-2.5 opacity-50 flex-shrink-0"
                />
              </a>
            ) : (
              <span className="flex items-center gap-1">
                <span className="truncate">{consignment.msNumber}</span>
                <FontAwesomeIcon
                  icon={faArrowUpRightFromSquare}
                  className="w-2.5 h-2.5 opacity-50 flex-shrink-0"
                />
              </span>
            )}
          </OverflowTooltip>
        </td>

        {/* Despatch Date */}
        <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">
          {(consignment.despatch_date || "").split(" ")[0]}
        </td>

        {/* Connote */}
        <td className="px-3 py-2.5 text-xs text-gray-700 font-mono max-w-[90px]">
          <OverflowTooltip value={consignment.connote} textClassName="font-mono">
            {consignment.connote}
          </OverflowTooltip>
        </td>

        {/* Parent Company */}
        <td className="px-3 py-2.5 text-xs text-gray-700 max-w-[130px]">
          <OverflowTooltip value={parentName}>{parentName}</OverflowTooltip>
        </td>

        {/* Child Company */}
        <td className="px-3 py-2.5 text-xs text-gray-700 max-w-[150px]">
          <OverflowTooltip value={companyName}>
            {companyName}
          </OverflowTooltip>
        </td>

        {/* Service Type */}
        <td className="px-3 py-2.5 text-xs text-gray-700 max-w-[110px]">
          <OverflowTooltip value={consignment.service}>
            {consignment.service}
          </OverflowTooltip>
        </td>

        {/* Carrier Name */}
        <td className="px-3 py-2.5 text-xs text-gray-700 max-w-[130px]">
          <OverflowTooltip value={consignment.carrier}>
            {consignment.carrier}
          </OverflowTooltip>
        </td>

        {/* Receiver */}
        <td className="px-3 py-2.5 text-xs text-gray-700 max-w-[250px]">
          <OverflowTooltip value={consignment.receiver}>
            {consignment.receiver}
          </OverflowTooltip>
        </td>

        {/* Completed Date */}
        <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">
          {consignment.completedDate || "—"}
        </td>

        {/* Latest Scanned Event */}
        <td className="px-3 py-2.5">
          <div className="flex items-start gap-2">
            <button
              type="button"
              onClick={() => setIsExpanded((p) => !p)}
              className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded transition-colors ${
                isExpanded
                  ? "bg-[#144677] text-white"
                  : "text-gray-400 hover:bg-[#144677]/10 hover:text-[#144677]"
              }`}
              title={isExpanded ? "Hide scan events" : "Show scan events"}
            >
              <FontAwesomeIcon
                icon={isExpanded ? faChevronDown : faChevronRight}
                className="h-2.5 w-2.5"
              />
            </button>
            <div className="min-w-0">
              <span
                className={`inline-flex max-w-full items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${badge}`}
              >
                <span className="truncate">{latestScanLabel}</span>
              </span>
              {/* <p className="mt-1 text-[10px] text-gray-400 whitespace-nowrap">
                {latestScanTime || "-"}
              </p> */}
            </div>
          </div>
        </td>

        {/* ETA */}
        <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">
          {((consignment.etaDate || consignment.eta || "").split(" ")[0]) || "—"}
        </td>

        {/* Special Instruction */}
        <td className="px-3 py-2.5 text-xs text-gray-600 max-w-[150px]">
          {specialInstruction ? (
            <div className="relative inline-block">
              <button
                ref={specialInstructionBtnRef}
                type="button"
                onClick={openSpecialInstruction}
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 transition-colors ${
                  specialInstructionPos
                    ? "bg-blue-100 text-[#144677] ring-blue-300"
                    : "bg-blue-50 text-[#144677] ring-blue-200 hover:bg-blue-100"
                }`}
              >
                Yes
              </button>
              {specialInstructionPos && createPortal(
                <div
                  ref={specialInstructionPopoverRef}
                  style={{ top: specialInstructionPos.top, left: specialInstructionPos.left }}
                  className="fixed z-[9999] w-64 max-h-40 overflow-y-auto rounded-lg border border-gray-200 bg-white p-3 text-[11px] leading-relaxed text-gray-700 shadow-xl"
                >
                  {specialInstruction}
                </div>,
                document.body
              )}
            </div>
          ) : (
            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
              No
            </span>
          )}
        </td>

        {/* Attachment */}
        <td className="px-3 py-2.5 text-xs text-gray-600 max-w-[110px]">
          {attachments.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {attachments.slice(0, 2).map((attachment, index) =>
                attachment.url ? (
                  <a
                    key={`${attachment.label}-${index}`}
                    href={attachment.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-700 ring-1 ring-violet-200 hover:bg-violet-100"
                  >
                    {attachment.label}
                  </a>
                ) : (
                  <span
                    key={`${attachment.label}-${index}`}
                    className="inline-flex items-center rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-700 ring-1 ring-violet-200"
                  >
                    {attachment.label}
                  </span>
                )
              )}
              {attachments.length > 2 ? (
                <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600 ring-1 ring-gray-200">
                  +{attachments.length - 2}
                </span>
              ) : null}
            </div>
          ) : attachmentCount > 0 ? (
            <span className="inline-flex items-center rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-700 ring-1 ring-violet-200">
              {attachmentCount} file{attachmentCount !== 1 ? "s" : ""}
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
              No
            </span>
          )}
        </td>

        {/* KPI Measure */}
        <td className="px-3 py-2.5 text-center">
          <span
            className={`inline-flex min-w-7 items-center justify-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${
              !kpiMeasureHasValue
                ? "bg-slate-100 text-slate-700 ring-slate-200"
                : kpiMeasureIsLate
                ? "bg-red-100 text-red-700 ring-red-200"
                : "bg-emerald-100 text-emerald-700 ring-emerald-200"
            }`}
          >
            {kpiMeasureDisplay}
          </span>
        </td>

        {/* Service Flags */}
        <td className="px-3 py-2.5 text-xs text-gray-600 max-w-[150px]">
          {hasServiceFlags ? (
            <div className="flex flex-wrap gap-1">
              {serviceFlags.slice(0, 3).map((flag, index) => (
                <span
                  key={`${flag}-${index}`}
                  className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700 ring-1 ring-amber-200"
                >
                  {flag}
                </span>
              ))}
              {serviceFlags.length > 3 ? (
                <span
                  className="relative inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600 ring-1 ring-gray-200 cursor-default group"
                  title={serviceFlags.slice(3).join(", ")}
                >
                  +{serviceFlags.length - 3}
                  <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:flex flex-col gap-0.5 bg-gray-800 text-white text-[10px] rounded-lg px-2.5 py-2 shadow-lg z-50 whitespace-nowrap min-w-max">
                    {serviceFlags.slice(3).map((flag, i) => (
                      <span key={i}>{flag}</span>
                    ))}
                    <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
                  </span>
                </span>
              ) : null}
            </div>
          ) : (
            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
              None
            </span>
          )}
        </td>

        {/* KPI Hit */}
        <td className="px-3 py-2.5">
          <span
            className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ring-1 ${
              !hasKpiHitValue
                ? "bg-gray-100 text-gray-500 ring-gray-200"
                : consignment.kpiHit
                ? "bg-emerald-100 text-emerald-700 ring-emerald-200"
                : "bg-red-100 text-red-700 ring-red-200"
            }`}
          >
            {kpiHitLabel}
          </span>
        </td>

        {/* Processed */}
        <td className="px-3 py-2.5">
          <span
            className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ring-1 ${
              !hasIsProcessedValue
                ? "bg-gray-100 text-gray-500 ring-gray-200"
                : consignment.isProcessed
                ? "bg-emerald-100 text-emerald-700 ring-emerald-200"
                : "bg-red-100 text-red-700 ring-red-200"
            }`}
          >
            {isProcessedLabel}
          </span>
        </td>

        {/* Update History */}
        <td className="px-3 py-2.5">
          {hasKpiUpdateHistory ? (
            <div className="relative inline-block">
              <button
                ref={kpiHistoryBtnRef}
                type="button"
                onClick={openKpiHistory}
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 transition-colors ${
                  kpiHistoryPos
                    ? "bg-sky-100 text-sky-700 ring-sky-300"
                    : "bg-sky-50 text-sky-700 ring-sky-200 hover:bg-sky-100"
                }`}
              >
                View
              </button>
              {kpiHistoryPos && createPortal(
                <div
                  ref={kpiHistoryPopoverRef}
                  style={{ top: kpiHistoryPos.top, left: kpiHistoryPos.left }}
                  className="fixed z-[9999] w-72 rounded-lg border border-gray-200 bg-white p-3 text-[11px] leading-relaxed text-gray-700 shadow-xl"
                >
                  <p className="mb-2 text-xs font-semibold text-gray-900">
                    KPI Update History
                  </p>
                  <div className="max-h-64 space-y-3 overflow-y-auto pr-1">
                    {sortedKpiUpdateHistoryEntries.map((entry, index) => (
                      <div
                        key={`${entry.updatedAt}-${entry.updatedBy}-${index}`}
                        className="border-b border-gray-100 pb-2 last:border-b-0 last:pb-0"
                      >
                        <p>
                          <span className="font-semibold">Source:</span>{" "}
                          {formatHistorySource(entry.source)}
                        </p>
                        <p>
                          <span className="font-semibold">Updated By:</span>{" "}
                          {entry.updatedBy}
                        </p>
                        <p>
                          <span className="font-semibold">Updated At:</span>{" "}
                          {formatEventTime(entry.updatedAt)}
                        </p>
                        <p>
                          <span className="font-semibold">Changed From:</span>{" "}
                          {entry.changedFrom} {"→"} {entry.changedTo}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>,
                document.body
              )}
            </div>
          ) : (
            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
              No
            </span>
          )}
        </td>

        {/* Reason */}
        <td className="px-3 py-2.5 text-xs text-gray-600 max-w-[300px]">
          <OverflowTooltip value={hasReason ? reasonLabel : ""} fallback="-">
            {reasonLabel}
          </OverflowTooltip>
        </td>

      </tr>

      {/* ── Expanded row ──────────────────────────────────────────── */}
      {isExpanded && (
        <tr className="bg-slate-50/80">
          <td colSpan={COL_SPAN} className="px-6 py-5 border-b border-gray-200">
            <div className="w-full min-w-0">
              {/* Scan events timeline */}
              <div className="w-full min-w-0">
                <p className="text-[10px] font-bold text-[#144677] uppercase tracking-wider mb-3">
                  Scan Events ({scanEvents.length})
                </p>
                {scanEvents.length === 0 ? (
                  <p className="text-xs text-gray-400">No scan events.</p>
                ) : (
                  <HorizontalTimeline events={scanEvents} />
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function normalizeAttachments(value) {
  if (!value) return [];

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    return [{ label: trimmed, url: isUrl(trimmed) ? trimmed : "" }];
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (!item) return null;
        if (typeof item === "string") {
          const trimmed = item.trim();
          if (!trimmed) return null;
          return { label: trimmed, url: isUrl(trimmed) ? trimmed : "" };
        }

        const label =
          item.label || item.name || item.filename || item.fileName || item.url || "";
        const url = item.url || item.href || "";
        if (!label && !url) return null;
        return { label: label || "Attachment", url };
      })
      .filter(Boolean);
  }

  if (typeof value === "object") {
    const label =
      value.label || value.name || value.filename || value.fileName || value.url || "";
    const url = value.url || value.href || "";
    if (!label && !url) return [];
    return [{ label: label || "Attachment", url }];
  }

  return [];
}

function normalizeScanEvents(value) {
  if (!Array.isArray(value)) return [];

  return value.map((event) => {
    if (!event || typeof event !== "object") {
      return { detail: "", event: "", time: "" };
    }

    return {
      ...event,
      detail: event.detail || event.tracking_status || "",
      event: event.event || event.tracking_status || "",
      time: formatEventTime(event.time || event.event_date_local),
      carrierStatus: event.carrierStatus || event.carrier_status || "",
      statusInformation: event.statusInformation || event.status_information || "",
      updatedBy: event.updatedBy || event.updated_by || "",
    };
  });
}

function normalizeAttachmentCount(value) {
  const count = Number(value);
  return Number.isFinite(count) && count > 0 ? count : 0;
}

function normalizeServiceFlags(value) {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return item.trim();
        if (item && typeof item === "object") {
          return item.label || item.name || item.value || "";
        }
        return "";
      })
      .filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
}

function isUrl(value = "") {
  return /^https?:\/\//i.test(value);
}

function formatKpiMeasureValue(value) {
  if (value === null || value === undefined || value === "") {
    return { kpiMeasureDisplay: "-", kpiMeasureIsLate: false, kpiMeasureHasValue: false };
  }
  const num = Number(value);
  if (Number.isNaN(num)) {
    return { kpiMeasureDisplay: String(value), kpiMeasureIsLate: false, kpiMeasureHasValue: true };
  }
  const isLate = num > 0;
  return {
    kpiMeasureDisplay: `${num} (${isLate ? "Late" : "On Time"})`,
    kpiMeasureIsLate: isLate,
    kpiMeasureHasValue: true,
  };
}

function OverflowTooltip({
  value,
  children,
  fallback = "-",
  textClassName = "",
}) {
  const text = typeof value === "string" ? value.trim() : value;
  const hasValue = !!text;

  if (!hasValue) {
    return <span className="block truncate text-gray-300">{fallback}</span>;
  }

  return (
    <div className="relative group">
      <span className={`block truncate ${textClassName}`}>{children}</span>
      <div className="pointer-events-none absolute left-0 top-full z-[9999] mt-1 hidden w-max max-w-[240px] rounded-md bg-gray-900 px-2 py-1 text-[10px] leading-tight text-white shadow-lg group-hover:block">
        {text}
      </div>
    </div>
  );
}

/* ── Horizontal scan events timeline ─────────────────────────────── */
function HorizontalTimeline({ events }) {
  const displayed = events;

  return (
    <div className="overflow-x-auto pb-2" style={{ maxWidth: 'calc(100vw - 260px)' }}>
      <div className="flex items-start gap-0 min-w-max">
        {displayed.map((event, idx) => {
          const color = getScanEventColor(event.detail);
          const isLast = idx === displayed.length - 1;
          return (
            <div key={idx} className="flex items-start">
              {/* Node */}
              <div className="flex flex-col items-center">
                <div
                  className={`w-3 h-3 rounded-full flex-shrink-0 ring-2 ring-white shadow-sm ${color.dot}`}
                />
                <div className="mt-2 text-center px-1">
                  <span
                    className={`inline-block px-1.5 py-0.5 text-[9px] font-semibold rounded whitespace-nowrap ${color.bg} ${color.text}`}
                  >
                    {event.detail}
                  </span>
                  <p className="text-[9px] text-gray-400 mt-0.5 whitespace-nowrap">
                    {event.time}
                  </p>
                  <div className="mt-1 grid max-w-[260px] gap-0.5 text-left text-[9px] leading-snug text-gray-500">
                    <TimelineInfo label="Carrier" value={event.carrierStatus} />
                    <TimelineInfo label="Info" value={event.statusInformation} />
                    <TimelineInfo label="By" value={event.updatedBy} />
                  </div>
                </div>
              </div>
              {/* Connector line */}
              {!isLast && (
                <div className="w-12 h-px bg-gray-300 mt-1.5 flex-shrink-0" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TimelineInfo({ label, value }) {
  if (!value) return null;
  return (
    <p className="max-w-[260px] whitespace-normal break-words">
      <span className="font-semibold text-gray-600">{label}:</span> {value}
    </p>
  );
}
